// 下拉多选组件的客户端逻辑（配合 core_components.ex 的 dropdown_multi_select）。
//
// 设计要点（为什么这么写）：
//   - 值保存在一个隐藏的 <select multiple> 里，作为「当前已选集合」的唯一来源；
//     服务端按 selected 渲染初始状态，hook 在客户端切换 option.selected。
//   - 增删 / 勾选时，从被点元素的 data-value 直接读值（getAttribute），而不是依赖
//     phx-value-value 这种会随 LiveView morphdom diff 流动的属性——后者在「无 id 列表
//     + 结构翻转」时会被 diff 掉，导致发出空 value。data-value 是静态属性，点击瞬间读死。
//   - 切换后派发原生 input/change 事件，让所在表单的 phx-change 统一接收选中集合，
//     值走标准表单参数（与项目里 required_outputs 的处理方式一致），无需 pushEvent。
const DropdownMultiSelect = {
	mounted() {
		const el = this.el;
		const selectEl = el.querySelector("select");
		const search = el.querySelector(".dms-search");
		const dropdown = el.querySelector(".dms-dropdown");
		const noMatch = el.querySelector(".dms-no-match");
		// disabled（非草稿）模式下不渲染搜索框 / 下拉，无需绑定任何交互
		if (!search || !dropdown) return;
		let activeIndex = -1;
		const findOption = (val) => Array.from(selectEl.options).find((o) => o.value === val);
		const getItems = () => el.querySelectorAll(".dms-item");
		const getVisibleItems = () => Array.from(getItems()).filter((i) => !i.classList.contains("hidden"));
		// 派发原生事件，由所在表单的 phx-change 接收整张表单（含本 select 的已选集合）
		const triggerChange = () => {
			selectEl.dispatchEvent(new Event("input", { bubbles: true }));
			selectEl.dispatchEvent(new Event("change", { bubbles: true }));
		};
		const setActive = (index) => {
			const visible = getVisibleItems();
			getItems().forEach((i) => i.classList.remove("bg-base-300"));
			if (index >= 0 && index < visible.length) {
				activeIndex = index;
				visible[activeIndex].classList.add("bg-base-300");
				visible[activeIndex].scrollIntoView({ block: "nearest" });
			} else {
				activeIndex = -1;
			}
		};
		// 按搜索词过滤下拉项；无匹配时显示提示。分组头随组内可见性显隐（无组头时 no-op）
		const refresh = () => {
			const q = search.value.toLowerCase().trim();
			let visible = 0;
			getItems().forEach((item) => {
				const label = (item.getAttribute("data-label") || "").toLowerCase();
				const val = (item.getAttribute("data-value") || "").toLowerCase();
				const match = q === "" || label.includes(q) || val.includes(q);
				item.classList.toggle("hidden", !match);
				if (match) visible++;
			});
			el.querySelectorAll(".dms-group-label").forEach((header) => {
				const group = header.getAttribute("data-group");
				const hasVisible = Array.from(getItems()).some((item) => item.getAttribute("data-group") === group && !item.classList.contains("hidden"));
				header.classList.toggle("hidden", !hasVisible);
			});
			if (noMatch) noMatch.classList.toggle("hidden", visible > 0);
			setActive(-1);
		};
		const show = () => {
			dropdown.classList.remove("hidden");
			refresh();
		};
		const hide = () => dropdown.classList.add("hidden");
		const toggleOption = (val) => {
			const opt = findOption(val);
			if (!opt) return;
			opt.selected = !opt.selected;
			search.value = "";
			refresh();
			search.focus();
			triggerChange();
		};
		const removeValue = (val) => {
			const opt = findOption(val);
			if (opt && opt.selected) {
				opt.selected = false;
				triggerChange();
				search.focus();
			}
		};
		// 点击容器空白处 → 聚焦搜索框并展开下拉
		// 关键：选中某项后 triggerChange 会触发服务端重渲染，而 LiveView 重渲染时会保持
		// search 的焦点，于是再次点击 search 不会重新触发 focus 事件。因此这里显式 show()，
		// 而不是只靠下面的 focus 监听来展开。
		el.addEventListener("click", (e) => {
			if (e.target.closest(".dms-remove")) return;
			if (e.target.closest(".dms-dropdown")) return;
			search.focus();
			show();
		});
		search.addEventListener("focus", show);
		search.addEventListener("input", () => {
			if (dropdown.classList.contains("hidden")) show();
			refresh();
		});
		// 点击下拉项：mousedown 防止失焦，click 切换选中
		dropdown.addEventListener("mousedown", (e) => {
			if (e.target.closest(".dms-item")) e.preventDefault();
		});
		dropdown.addEventListener("click", (e) => {
			const item = e.target.closest(".dms-item");
			if (item) {
				e.preventDefault();
				e.stopPropagation();
				toggleOption(item.getAttribute("data-value"));
			}
		});
		// 点击 chip 的 x 删除
		el.addEventListener("click", (e) => {
			const btn = e.target.closest(".dms-remove");
			if (btn) {
				e.preventDefault();
				e.stopPropagation();
				removeValue(btn.getAttribute("data-value"));
			}
		});
		// 点击外部关闭下拉
		const handleClickAway = (e) => {
			if (!el.contains(e.target)) hide();
		};
		document.addEventListener("click", handleClickAway);
		// 键盘：上下选择 / 回车确认 / ESC 关闭 / 退格删末项
		search.addEventListener("keydown", (e) => {
			const visible = getVisibleItems();
			if (e.key === "ArrowDown") {
				e.preventDefault();
				if (dropdown.classList.contains("hidden")) show();
				if (visible.length > 0) setActive((activeIndex + 1) % visible.length);
			} else if (e.key === "ArrowUp") {
				e.preventDefault();
				if (visible.length > 0) setActive((activeIndex - 1 + visible.length) % visible.length);
			} else if (e.key === "Enter") {
				e.preventDefault();
				if (activeIndex >= 0 && activeIndex < visible.length) toggleOption(visible[activeIndex].getAttribute("data-value"));
			} else if (e.key === "Escape") {
				hide();
				search.blur();
			} else if (e.key === "Backspace" && search.value === "") {
				const opts = Array.from(selectEl.options).filter((o) => o.selected);
				if (opts.length > 0) {
					opts[opts.length - 1].selected = false;
					triggerChange();
				}
			}
		});
		this._handleClickAway = handleClickAway;
	},
	destroyed() {
		if (this._handleClickAway) document.removeEventListener("click", this._handleClickAway);
	}
};
export default DropdownMultiSelect;
